import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListChargeTypes,
  useCreateChargeType,
  useUpdateChargeType,
  useDeleteChargeType,
  getListChargeTypesQueryKey,
} from "@workspace/api-client-react";
import type { InventoryChargeType } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

const GST_RATES = [0, 5, 12, 18, 28, 40] as const;

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

interface FormState {
  name: string;
  defaultGstRate: number;
  status: "Active" | "Archived";
}

const EMPTY: FormState = { name: "", defaultGstRate: 18, status: "Active" };

export function ChargeTypesTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editRow, setEditRow] = useState<InventoryChargeType | null>(null);
  const [deleteRow, setDeleteRow] = useState<InventoryChargeType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);

  const { data: rows, isLoading } = useListChargeTypes();
  const createMutation = useCreateChargeType();
  const updateMutation = useUpdateChargeType();
  const deleteMutation = useDeleteChargeType();

  const sheetOpen = openCreate || editRow !== null;

  useEffect(() => {
    if (editRow) {
      setForm({
        name: editRow.name,
        defaultGstRate: editRow.defaultGstRate != null ? Number(editRow.defaultGstRate) : 0,
        status: (editRow.status as "Active" | "Archived") ?? "Active",
      });
    } else if (openCreate) {
      setForm(EMPTY);
    }
  }, [editRow, openCreate]);

  const q = search.trim().toLowerCase();
  const filtered = (rows ?? []).filter((c) => !q || c.name.toLowerCase().includes(q));

  function closeSheet() {
    setOpenCreate(false);
    setEditRow(null);
    setForm(EMPTY);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ title: "Name is required.", variant: "destructive" });
      return;
    }
    try {
      if (editRow) {
        await updateMutation.mutateAsync({
          id: editRow.id,
          data: { name: form.name.trim(), defaultGstRate: form.defaultGstRate, status: form.status },
        });
        toast({ title: "Charge type updated." });
      } else {
        await createMutation.mutateAsync({
          data: { name: form.name.trim(), defaultGstRate: form.defaultGstRate, status: form.status },
        });
        toast({ title: "Charge type added." });
      }
      queryClient.invalidateQueries({ queryKey: getListChargeTypesQueryKey() });
      closeSheet();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      toast({
        title: status === 409 ? "That charge type already exists." : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteRow) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteRow.id });
      toast({ title: `${deleteRow.name} deleted.` });
      queryClient.invalidateQueries({ queryKey: getListChargeTypesQueryKey() });
      setDeleteRow(null);
    } catch {
      toast({ title: "Could not delete. Please try again.", variant: "destructive" });
      setDeleteRow(null);
    }
  }

  return (
    <>
      <div className="mb-4 flex gap-2 items-center flex-wrap">
        <Input
          placeholder="Search charge types…"
          className="w-full sm:w-[280px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <p className="text-xs text-muted-foreground ml-auto">
          Used to add delivery / handling / platform fees on a stock-in.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Charge Type</TableHead>
                <TableHead className="w-[120px] text-right">Default GST %</TableHead>
                <TableHead className="w-[90px]">Status</TableHead>
                <TableHead className="w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(4)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    No charge types yet. Click “Add Charge Type”.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {c.defaultGstRate != null ? `${Number(c.defaultGstRate)}%` : "—"}
                    </TableCell>
                    <TableCell>
                      {c.status === "Archived" ? (
                        <Badge variant="outline" className="text-muted-foreground">Archived</Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => { setOpenCreate(false); setEditRow(c); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteRow(c)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent side="right" className="w-[420px] flex flex-col">
          <SheetHeader>
            <SheetTitle>{editRow ? `Edit ${editRow.name}` : "Add Charge Type"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-4 py-4">
            <div>
              <label className="text-xs font-semibold">Name <span className="text-destructive">*</span></label>
              <Input
                placeholder="e.g. Platform fee"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Default GST %</label>
              <Select value={String(form.defaultGstRate)} onValueChange={(v) => setForm((p) => ({ ...p, defaultGstRate: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Suggested when this charge is added to a receipt.</p>
            </div>
            <div>
              <label className="text-xs font-semibold">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v as "Active" | "Archived" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={closeSheet}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending ? "Saving…" : (editRow ? "Save Changes" : "Add Charge Type")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteRow !== null} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteRow?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the charge type from the picker. Stock-ins that already used it keep their
              saved charge lines. This can&rsquo;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
